import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { ToastContainer } from './components/shared/Toast';
import { MobileNav } from './components/shared/MobileNav';
import { Skeleton } from './components/shared/Skeleton';
import { ErrorBoundary } from './components/shared/ErrorBoundary';
// Eager load critical pages
import { LandingPage } from './pages/LandingPage';
import { LoginForm } from './components/auth/LoginForm';
import { SignupForm } from './components/auth/SignupForm';
// Lazy load dashboard pages (code splitting)
const GetStarted = lazy(() => import('./pages/shared/GetStarted').then(m => ({
  default: m.GetStarted
})));
const TenantDashboard = lazy(() => import('./pages/tenant/TenantDashboard').then(m => ({
  default: m.TenantDashboard
})));
const TenantOnboarding = lazy(() => import('./pages/tenant/TenantOnboarding').then(m => ({
  default: m.TenantOnboarding
})));
const RentRequestCreate = lazy(() => import('./pages/tenant/RentRequestCreate').then(m => ({
  default: m.RentRequestCreate
})));
const PaymentSchedule = lazy(() => import('./pages/tenant/PaymentSchedule').then(m => ({
  default: m.PaymentSchedule
})));
const TenantSettings = lazy(() => import('./pages/tenant/TenantSettings').then(m => ({
  default: m.TenantSettings
})));
const DocumentVault = lazy(() => import('./pages/tenant/DocumentVault').then(m => ({
  default: m.DocumentVault
})));
const PayrollIntegration = lazy(() => import('./pages/tenant/PayrollIntegration').then(m => ({
  default: m.PayrollIntegration
})));
const MaintenanceRequests = lazy(() => import('./pages/tenant/MaintenanceRequests').then(m => ({
  default: m.MaintenanceRequests
})));
const RentCertificate = lazy(() => import('./pages/tenant/RentCertificate').then(m => ({
  default: m.RentCertificate
})));
const RentHistory = lazy(() => import('./pages/tenant/RentHistory').then(m => ({
  default: m.RentHistory
})));
const PaymentFlexibility = lazy(() => import('./pages/tenant/PaymentFlexibility').then(m => ({
  default: m.PaymentFlexibility
})));
const PaymentOptions = lazy(() => import('./pages/tenant/PaymentOptions').then(m => ({
  default: m.PaymentOptions
})));
const PaymentSuccess = lazy(() => import('./pages/tenant/PaymentSuccess').then(m => ({
  default: m.PaymentSuccess
})));
const CreditScore = lazy(() => import('./pages/tenant/CreditScore').then(m => ({
  default: m.CreditScore
})));
const LandlordDashboard = lazy(() => import('./pages/landlord/LandlordDashboard').then(m => ({
  default: m.LandlordDashboard
})));
const LandlordOnboarding = lazy(() => import('./pages/landlord/LandlordOnboarding').then(m => ({
  default: m.LandlordOnboarding
})));
const PropertyManagement = lazy(() => import('./pages/landlord/PropertyManagement').then(m => ({
  default: m.PropertyManagement
})));
const LeaseGenerator = lazy(() => import('./pages/landlord/LeaseGenerator').then(m => ({
  default: m.LeaseGenerator
})));
const ApplicationBuilder = lazy(() => import('./pages/landlord/ApplicationBuilder').then(m => ({
  default: m.ApplicationBuilder
})));
const PortfolioOverview = lazy(() => import('./pages/landlord/PortfolioOverview').then(m => ({
  default: m.PortfolioOverview
})));
const PropertyListingForm = lazy(() => import('./pages/landlord/PropertyListingForm').then(m => ({
  default: m.PropertyListingForm
})));
const VerificationStatus = lazy(() => import('./pages/landlord/VerificationStatus').then(m => ({
  default: m.VerificationStatus
})));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard').then(m => ({
  default: m.AdminDashboard
})));
const TenantVerification = lazy(() => import('./pages/admin/TenantVerification').then(m => ({
  default: m.TenantVerification
})));
const Analytics = lazy(() => import('./pages/admin/Analytics').then(m => ({
  default: m.Analytics
})));
const RiskManagement = lazy(() => import('./pages/admin/RiskManagement').then(m => ({
  default: m.RiskManagement
})));
const PropertyVerification = lazy(() => import('./pages/admin/PropertyVerification').then(m => ({
  default: m.PropertyVerification
})));
const EscrowManagement = lazy(() => import('./pages/admin/EscrowManagement').then(m => ({
  default: m.EscrowManagement
})));
const DocumentSigning = lazy(() => import('./pages/shared/DocumentSigning').then(m => ({
  default: m.DocumentSigning
})));
const PlatformInsights = lazy(() => import('./pages/shared/PlatformInsights').then(m => ({
  default: m.PlatformInsights
})));
const EmployerDashboard = lazy(() => import('./pages/employer/EmployerDashboard').then(m => ({
  default: m.EmployerDashboard
})));
const HelpCenter = lazy(() => import('./pages/shared/HelpCenter').then(m => ({
  default: m.HelpCenter
})));
const DeveloperDocs = lazy(() => import('./pages/shared/DeveloperDocs').then(m => ({
  default: m.DeveloperDocs
})));
// Loading fallback component
function PageLoader() {
  return <div className="min-h-screen bg-gray-50 dark:bg-gray-900" data-id="element-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1">
        <Skeleton className="h-8 w-48 mb-6" data-id="element-2" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8" data-id="element-3">
          <Skeleton className="h-32" data-id="element-4" />
          <Skeleton className="h-32" data-id="element-5" />
          <Skeleton className="h-32" data-id="element-6" />
          <Skeleton className="h-32" data-id="element-7" />
        </div>
        <Skeleton className="h-96" data-id="element-8" />
      </div>
    </div>;
}
// Protected Route Wrapper
const ProtectedRoute = ({
  children,
  allowedRole
}: {
  children: React.ReactNode;
  allowedRole?: string;
}) => {
  const {
    user,
    isLoading
  } = useAuth();
  if (isLoading) {
    return <PageLoader data-id="element-9" />;
  }
  if (!user) {
    return <Navigate to="/login" replace data-id="element-10" />;
  }
  if (allowedRole && user.role !== allowedRole) {
    return <Navigate to="/" replace data-id="element-11" />;
  }
  return <>{children}</>;
};
export function App() {
  const {
    user
  } = useAuth();
  return <ErrorBoundary data-id="element-12">
      <Router data-id="element-13">
        <ToastContainer data-id="element-14" />
        <Routes data-id="element-15">
          <Route path="/" element={<LandingPage data-id="element-17" />} data-id="element-16" />
          <Route path="/get-started" element={<Suspense fallback={<PageLoader data-id="element-20" />} data-id="element-19">
                <GetStarted data-id="element-21" />
              </Suspense>} data-id="element-18" />
          <Route path="/login" element={<LoginForm data-id="element-23" />} data-id="element-22" />
          <Route path="/signup" element={<SignupForm data-id="element-25" />} data-id="element-24" />

          {/* Shared Routes */}
          <Route path="/help" element={<ProtectedRoute data-id="element-27">
                <Suspense fallback={<PageLoader data-id="element-29" />} data-id="element-28">
                  <HelpCenter data-id="element-30" />
                </Suspense>
              </ProtectedRoute>} data-id="element-26" />
          <Route path="/docs" element={<ProtectedRoute data-id="element-32">
                <Suspense fallback={<PageLoader data-id="element-34" />} data-id="element-33">
                  <DeveloperDocs data-id="element-35" />
                </Suspense>
              </ProtectedRoute>} data-id="element-31" />

          {/* Tenant Routes */}
          <Route path="/tenant/dashboard" element={<ProtectedRoute allowedRole="tenant" data-id="element-37">
                <Suspense fallback={<PageLoader data-id="element-39" />} data-id="element-38">
                  <TenantDashboard data-id="element-40" />
                </Suspense>
              </ProtectedRoute>} data-id="element-36" />
          <Route path="/tenant/onboarding" element={<ProtectedRoute allowedRole="tenant" data-id="element-42">
                <Suspense fallback={<PageLoader data-id="element-44" />} data-id="element-43">
                  <TenantOnboarding data-id="element-45" />
                </Suspense>
              </ProtectedRoute>} data-id="element-41" />
          <Route path="/tenant/rent-request" element={<ProtectedRoute allowedRole="tenant" data-id="element-47">
                <Suspense fallback={<PageLoader data-id="element-49" />} data-id="element-48">
                  <RentRequestCreate data-id="element-50" />
                </Suspense>
              </ProtectedRoute>} data-id="element-46" />
          <Route path="/tenant/payment-schedule" element={<ProtectedRoute allowedRole="tenant" data-id="element-52">
                <Suspense fallback={<PageLoader data-id="element-54" />} data-id="element-53">
                  <PaymentSchedule data-id="element-55" />
                </Suspense>
              </ProtectedRoute>} data-id="element-51" />
          <Route path="/tenant/payment-options" element={<ProtectedRoute allowedRole="tenant" data-id="element-57">
                <Suspense fallback={<PageLoader data-id="element-59" />} data-id="element-58">
                  <PaymentOptions data-id="element-60" />
                </Suspense>
              </ProtectedRoute>} data-id="element-56" />
          <Route path="/tenant/payment-success" element={<ProtectedRoute allowedRole="tenant" data-id="element-62">
                <Suspense fallback={<PageLoader data-id="element-64" />} data-id="element-63">
                  <PaymentSuccess data-id="element-65" />
                </Suspense>
              </ProtectedRoute>} data-id="element-61" />
          <Route path="/tenant/settings" element={<ProtectedRoute allowedRole="tenant" data-id="element-67">
                <Suspense fallback={<PageLoader data-id="element-69" />} data-id="element-68">
                  <TenantSettings data-id="element-70" />
                </Suspense>
              </ProtectedRoute>} data-id="element-66" />
          <Route path="/tenant/documents" element={<ProtectedRoute allowedRole="tenant" data-id="element-72">
                <Suspense fallback={<PageLoader data-id="element-74" />} data-id="element-73">
                  <DocumentVault data-id="element-75" />
                </Suspense>
              </ProtectedRoute>} data-id="element-71" />
          <Route path="/tenant/payroll-integration" element={<ProtectedRoute allowedRole="tenant" data-id="element-77">
                <Suspense fallback={<PageLoader data-id="element-79" />} data-id="element-78">
                  <PayrollIntegration data-id="element-80" />
                </Suspense>
              </ProtectedRoute>} data-id="element-76" />
          <Route path="/tenant/maintenance" element={<ProtectedRoute allowedRole="tenant" data-id="element-82">
                <Suspense fallback={<PageLoader data-id="element-84" />} data-id="element-83">
                  <MaintenanceRequests data-id="element-85" />
                </Suspense>
              </ProtectedRoute>} data-id="element-81" />
          <Route path="/tenant/certificate" element={<ProtectedRoute allowedRole="tenant" data-id="element-87">
                <Suspense fallback={<PageLoader data-id="element-89" />} data-id="element-88">
                  <RentCertificate data-id="element-90" />
                </Suspense>
              </ProtectedRoute>} data-id="element-86" />
          <Route path="/tenant/rent-history" element={<ProtectedRoute allowedRole="tenant" data-id="element-92">
                <Suspense fallback={<PageLoader data-id="element-94" />} data-id="element-93">
                  <RentHistory data-id="element-95" />
                </Suspense>
              </ProtectedRoute>} data-id="element-91" />
          <Route path="/tenant/payment-flexibility" element={<ProtectedRoute allowedRole="tenant" data-id="element-97">
                <Suspense fallback={<PageLoader data-id="element-99" />} data-id="element-98">
                  <PaymentFlexibility data-id="element-100" />
                </Suspense>
              </ProtectedRoute>} data-id="element-96" />
          <Route path="/tenant/credit-score" element={<ProtectedRoute allowedRole="tenant" data-id="element-102">
                <Suspense fallback={<PageLoader data-id="element-104" />} data-id="element-103">
                  <CreditScore data-id="element-105" />
                </Suspense>
              </ProtectedRoute>} data-id="element-101" />

          {/* Landlord Routes */}
          <Route path="/landlord/dashboard" element={<ProtectedRoute allowedRole="landlord" data-id="element-107">
                <Suspense fallback={<PageLoader data-id="element-109" />} data-id="element-108">
                  <LandlordDashboard data-id="element-110" />
                </Suspense>
              </ProtectedRoute>} data-id="element-106" />
          <Route path="/landlord/onboarding" element={<ProtectedRoute allowedRole="landlord" data-id="element-112">
                <Suspense fallback={<PageLoader data-id="element-114" />} data-id="element-113">
                  <LandlordOnboarding data-id="element-115" />
                </Suspense>
              </ProtectedRoute>} data-id="element-111" />
          <Route path="/landlord/properties" element={<ProtectedRoute allowedRole="landlord" data-id="element-117">
                <Suspense fallback={<PageLoader data-id="element-119" />} data-id="element-118">
                  <PropertyManagement data-id="element-120" />
                </Suspense>
              </ProtectedRoute>} data-id="element-116" />
          <Route path="/landlord/lease-generator" element={<ProtectedRoute allowedRole="landlord" data-id="element-122">
                <Suspense fallback={<PageLoader data-id="element-124" />} data-id="element-123">
                  <LeaseGenerator data-id="element-125" />
                </Suspense>
              </ProtectedRoute>} data-id="element-121" />
          <Route path="/landlord/applications" element={<ProtectedRoute allowedRole="landlord" data-id="element-127">
                <Suspense fallback={<PageLoader data-id="element-129" />} data-id="element-128">
                  <ApplicationBuilder data-id="element-130" />
                </Suspense>
              </ProtectedRoute>} data-id="element-126" />
          <Route path="/landlord/portfolio" element={<ProtectedRoute allowedRole="landlord" data-id="element-132">
                <Suspense fallback={<PageLoader data-id="element-134" />} data-id="element-133">
                  <PortfolioOverview data-id="element-135" />
                </Suspense>
              </ProtectedRoute>} data-id="element-131" />
          <Route path="/landlord/property/new" element={<ProtectedRoute allowedRole="landlord" data-id="element-137">
                <Suspense fallback={<PageLoader data-id="element-139" />} data-id="element-138">
                  <PropertyListingForm data-id="element-140" />
                </Suspense>
              </ProtectedRoute>} data-id="element-136" />
          <Route path="/landlord/verification-status" element={<ProtectedRoute allowedRole="landlord" data-id="element-142">
                <Suspense fallback={<PageLoader data-id="element-144" />} data-id="element-143">
                  <VerificationStatus data-id="element-145" />
                </Suspense>
              </ProtectedRoute>} data-id="element-141" />

          {/* Admin Routes */}
          <Route path="/admin/dashboard" element={<ProtectedRoute allowedRole="admin" data-id="element-147">
                <Suspense fallback={<PageLoader data-id="element-149" />} data-id="element-148">
                  <AdminDashboard data-id="element-150" />
                </Suspense>
              </ProtectedRoute>} data-id="element-146" />
          <Route path="/admin/verification" element={<ProtectedRoute allowedRole="admin" data-id="element-152">
                <Suspense fallback={<PageLoader data-id="element-154" />} data-id="element-153">
                  <TenantVerification data-id="element-155" />
                </Suspense>
              </ProtectedRoute>} data-id="element-151" />
          <Route path="/admin/analytics" element={<ProtectedRoute allowedRole="admin" data-id="element-157">
                <Suspense fallback={<PageLoader data-id="element-159" />} data-id="element-158">
                  <Analytics data-id="element-160" />
                </Suspense>
              </ProtectedRoute>} data-id="element-156" />
          <Route path="/admin/risk-management" element={<ProtectedRoute allowedRole="admin" data-id="element-162">
                <Suspense fallback={<PageLoader data-id="element-164" />} data-id="element-163">
                  <RiskManagement data-id="element-165" />
                </Suspense>
              </ProtectedRoute>} data-id="element-161" />
          <Route path="/admin/property-verification" element={<ProtectedRoute allowedRole="admin" data-id="element-167">
                <Suspense fallback={<PageLoader data-id="element-169" />} data-id="element-168">
                  <PropertyVerification data-id="element-170" />
                </Suspense>
              </ProtectedRoute>} data-id="element-166" />
          <Route path="/admin/escrow-management" element={<ProtectedRoute allowedRole="admin" data-id="element-172">
                <Suspense fallback={<PageLoader data-id="element-174" />} data-id="element-173">
                  <EscrowManagement data-id="element-175" />
                </Suspense>
              </ProtectedRoute>} data-id="element-171" />
          <Route path="/documents/sign/:documentId" element={<Suspense fallback={<PageLoader data-id="element-178" />} data-id="element-177">
                <DocumentSigning data-id="element-179" />
              </Suspense>} data-id="element-176" />
          <Route path="/admin/insights" element={<ProtectedRoute allowedRole="admin" data-id="element-181">
                <Suspense fallback={<PageLoader data-id="element-183" />} data-id="element-182">
                  <PlatformInsights data-id="element-184" />
                </Suspense>
              </ProtectedRoute>} data-id="element-180" />

          {/* Employer Routes */}
          <Route path="/employer/dashboard" element={<ProtectedRoute allowedRole="employer" data-id="element-186">
                <Suspense fallback={<PageLoader data-id="element-188" />} data-id="element-187">
                  <EmployerDashboard data-id="element-189" />
                </Suspense>
              </ProtectedRoute>} data-id="element-185" />

          <Route path="*" element={<Navigate to="/" replace data-id="element-191" />} data-id="element-190" />
        </Routes>
        {/* Only show MobileNav for authenticated users */}
        {user && <MobileNav data-id="element-192" />}
      </Router>
    </ErrorBoundary>;
}